/**
 * admin-api 业务错误码 — admin 后台自家命名空间
 *
 * 与 dev-docs C 端错误码字典并列：dev-docs 的字典管 C 端 API 契约（novel-app-server），
 * 本文件管 admin-api 自家业务码（不进 dev-docs，避免双份维护漂移）；
 * 系统码复用 SYSTEM_ERROR_CODES，本文件只新增 admin 业务码
 *
 * 格式：{DOMAIN}_{REASON}，UPPER_SNAKE_CASE
 * DOMAIN 闭集：AUTH / RESOURCE / FEEDBACK / PUSH / OPS / FINANCE / BOOK / USER / APP / PAY / SYSTEM
 *
 * 新增业务码必须：① 在本文件登记 ② 注释 details shape ③ 注释客户端建议
 */

export const ADMIN_ERROR_CODES = {
  // ─────── AUTH 域（admin 鉴权 / 邀请 / 2FA） ───────
  /** 已登录但未完成 2FA；HTTP 422；details: 无 */
  AUTH_MFA_REQUIRED: 'AUTH_MFA_REQUIRED',
  /** 邮箱/密码不匹配（统一码避免账户枚举）；HTTP 422；details: 无 */
  AUTH_INVALID_CREDENTIALS: 'AUTH_INVALID_CREDENTIALS',
  /** 账号被锁（连续 5 次登录失败 / IP 1min 内 20 次失败）；HTTP 422；details: { unlock_at } */
  AUTH_LOCKED: 'AUTH_LOCKED',
  /** session_version 不匹配（改密 / 注销 / 风控 / logout-all-devices）；HTTP 401；details: 无 */
  AUTH_SESSION_REVOKED: 'AUTH_SESSION_REVOKED',
  /** TOTP code 错误；HTTP 422；details: { attempts_left? } */
  AUTH_MFA_INVALID: 'AUTH_MFA_INVALID',
  /** TOTP 错 3 次锁 5min；HTTP 422；details: { unlock_at } */
  AUTH_MFA_LOCKED: 'AUTH_MFA_LOCKED',
  /** 备份码错误；HTTP 422；details: { attempts_left? } */
  AUTH_BACKUP_CODE_INVALID: 'AUTH_BACKUP_CODE_INVALID',
  /** 邀请 token 不存在 / 已过期 / 已使用；HTTP 422；details: 无 */
  AUTH_INVITE_INVALID: 'AUTH_INVITE_INVALID',
  /** 邀请目标邮箱已注册 admin 账号；HTTP 422；details: 无 */
  AUTH_INVITE_EMAIL_TAKEN: 'AUTH_INVITE_EMAIL_TAKEN',
  /** 密码重置 token 无效；HTTP 422；details: 无 */
  AUTH_PASSWORD_RESET_INVALID: 'AUTH_PASSWORD_RESET_INVALID',
  /** refresh token 不存在 / 已撤销 / 已过期；HTTP 401；details: 无 */
  AUTH_REFRESH_INVALID: 'AUTH_REFRESH_INVALID',
  /** 鉴权类请求过于频繁；HTTP 422；details: { retry_after_sec } */
  AUTH_RATE_LIMITED: 'AUTH_RATE_LIMITED',

  // ─────── APP 域（ADM-APP-*） ───────
  /** App slug / bundle_id / package_name 冲突；HTTP 422；details: { conflict_field } */
  APP_CONFLICT: 'APP_CONFLICT',
  /** App 已停用（操作类接口拒绝）；HTTP 422；details: { app_id } */
  APP_INACTIVE: 'APP_INACTIVE',

  // ─────── PAY 域（ADM-PAY-*） ───────
  /** 支付页面为系统预置（is_built_in=true），禁止删除（仅可上下架 ADM-PAY-S002）；HTTP 422；details: { slug } */
  PAY_PAGE_BUILT_IN_LOCKED: 'PAY_PAGE_BUILT_IN_LOCKED',
  /** 删除支付页面时该页 scenario 仍被运营入口 / 下游消费方引用，拒删并提示先清理（ADM-PAY-S004，张培 #120；
   *  dev-docs #126 加第三类 popup scene；#228 决议 3：banner 改引 offer_id，外部引用收敛为 active app_payment_offers）；
   *  引用来源（终态，#243 移除迁移前 legacy push_task scenario scan 后）：active app_payment_offers（该 scenario、
   *  now<ends_at）；slug='operational_popup' 时另含 active app_operational_popup_scenes（该页是其 paywall placement）。
   *  banner/push 统一 offer_id 寻址、不再直接引用 scenario，其完整性由删 offer 校验承接（ADM-PAY-S005）。HTTP 422；
   *  details: { references: { kind: 'payment_offer' | 'operational_popup_scene'; id: string; label: string }[] } */
  PAY_PAGE_SCENARIO_REFERENCED: 'PAY_PAGE_SCENARIO_REFERENCED',
  /** 支付页处于下架态（is_active=false）时写 Paywall placement，拒绝（ADM-PAY-S002/S003；A8 §7.5 流程 ④）。
   *  下架已在 §7.3 清空本页全部 adapty_placement_id，此时写入等于绕过下架恢复 C 端 serving；传 null 清空同样拒
   *  —— 下架页的 placement 收敛入口是 §7.3 下架级联（一律执行清空），不经本端点；存量「显示下架却仍有
   *  placement」的页由运营点一次下架收敛。与 §7.3 下架级联共享 (app_id, scenario) advisory 锁、二者互斥。
   *  **scenario='operational_popup' 页不返本码**：该 scenario 走 §7.5「operational_popup 分支」，跳过上架前置态
   *  （页级 is_active 对它无执行点，与 §7.3 级联排除同口径）。
   *  HTTP 422；details: { scenario: string } */
  PAY_PAGE_INACTIVE: 'PAY_PAGE_INACTIVE',
  /** 写 scenario 引用时目标支付页 scenario 不存在或已删（防「删页后再建 scenario 引用指向已删 scenario」，adversarial r2）。
   *  HTTP 422；details: { app_id: string; scenario: string }。**当前无抛出点**：banner（#228）与 push topup_promo
   *  （PR #152）均已改 offer_id 寻址、不再写 (app_id, scenario) 引用，原 push 写侧断言 assertPaymentScenarioExists 已删；
   *  本码暂保留注册、随 #243 scenario 模型退役（删页护栏 scan 移除）一并清理 */
  PAY_PAGE_SCENARIO_NOT_FOUND: 'PAY_PAGE_SCENARIO_NOT_FOUND',
  /** 停用 / 删除运营支付活动 offer 时，该 offer_id 仍被 active 外部入口引用：active banner_slides
   *  （target_type='offer' AND is_active=true AND 未软删）或未投递（PENDING/SENDING）topup_promo push_tasks。
   *  删 / 停活动侧 reverse 护栏，杜绝悬空 offer_id 死链（ADM-PAY-S005，dev-docs A8 §6.6 ②；是 banner
   *  OPS_BANNER_OFFER_NOT_FOUND / push PUSH_OFFER_NOT_FOUND 建侧 forward 护栏的对偶）。HTTP 422；
   *  details: { offer_id: string; references: { kind: 'banner_slide' | 'push_task'; id: string }[] }
   *  （镜像 dev-docs 21-Admin错误码段 §2.5 / A8 §6.6）。
   *  客户端：先下架引用的 Banner（ADM-OPS-S004）/ 删除未投递推送任务（ADM-PUSH-S003），再停 / 删活动 */
  PAY_OFFER_REFERENCED: 'PAY_OFFER_REFERENCED',
  /** 拉取 Adapty catalog（GET /payments/adapty/catalog）/ 同步 placement 时该 App 无 active Adapty 凭证
   *  （adapty_app_credentials 无 active 行）；前端引导去「集成」页录入 Adapty Secret Key。HTTP 422；
   *  details: { app_id }。admin-own 业务码、dev-docs 收录义务 #229（同步提 dev-docs PR）。
   *  注：Adapty 上游故障（超时/网络/非2xx）走系统码 SERVICE_UNAVAILABLE（HTTP 503），不在此另设业务码 */
  PAY_ADAPTY_NOT_CONFIGURED: 'PAY_ADAPTY_NOT_CONFIGURED',
  /** 经 `PUT /payment-pages/:id/paywalls` 把 operational_popup 页某通道 adapty_placement_id 清为 null，清空后该
   *  scenario **不再有任何已填值通道行**，且该 App 生效层仍有 active app_operational_popup_scenes 依赖该落点
   *  （ADM-PAY-S004③，A8 §7.5「operational_popup 分支」/ §7.6）。仍有其它已填值通道时不触发。与删页
   *  PAY_PAGE_SCENARIO_REFERENCED 同判据（共用 findScenarioReferences 的 operational_popup_scene 分支）。
   *  HTTP 422；details: { references: { kind: 'operational_popup_scene'; id: string; label: string }[] }。
   *  客户端：先在运营弹窗下架依赖该落点的场景（ADM-OPS-S007），或保留至少一个已配通道。
   *  契约见 dev-docs 01-通用约定/21-Admin错误码段.md §2.5 */
  PAY_POPUP_PLACEMENT_REFERENCED: 'PAY_POPUP_PLACEMENT_REFERENCED',

  // ─────── Stripe direct Checkout App 配置（ADM-PAY-S007 / SRV-PAY-029，A8 §9）───────
  // 判定顺序（21-Admin错误码段 §2.5）：① config row / credential route 不存在 → NOT_CONFIGURED；
  // ② 本地字段缺失或本 App 无回跳 host 登记 → NOT_READY（host 不属于该 App 登记或 path 不符走 VALIDATION_FAILED/redirect_not_allowed）；
  // ③ 实时 Stripe credential / permission 失败 → CONNECTION_FAILED（上游不可达走 SERVICE_UNAVAILABLE）。
  /** 无未软删 config row，或当前环境无法解析 config 引用的 credential route；HTTP 422；
   *  details: { app_id: string; reason: 'CONFIG_MISSING' | 'CREDENTIAL_ROUTE_MISSING' }。
   *  **不得**返回 route 解析细节 */
  PAY_STRIPE_CHECKOUT_NOT_CONFIGURED: 'PAY_STRIPE_CHECKOUT_NOT_CONFIGURED',
  /** config 字段缺失、本环境未为该 App 登记支付回跳 host（`PAYMENT_RETURN_HOSTS`）或环境 credential 加密 master key 缺失；HTTP 422；
   *  details: { app_id: string; issues: { code: 'CONFIG_INCOMPLETE' | 'REDIRECT_ALLOWLIST_MISSING'
   *  | 'CREDENTIAL_STORE_UNAVAILABLE' }[] }。
   *  `CREDENTIAL_STORE_UNAVAILABLE` = 本环境未注入 `STRIPE_CHECKOUT_KEY_ENC_KEY`（`03-数据字典/70-产品配置.md`
   *  §2.2 `restricted_key_enc` 的 master key）：写侧无法加密落库、读侧无法解密取用，对 PUT / test / activate
   *  **对称失效**，且与具体 config 的 route 引用是否可解析无关 —— 故它**先于** `NOT_CONFIGURED` 的
   *  config 级判定（`01-通用约定/21-Admin错误码段.md` §2.5 判定顺序②）。
   *  product 的 vendor_product_id / stripe_price_id 映射缺失**不属于** App config 错误（由 composite routability fail-closed）*/
  PAY_STRIPE_CHECKOUT_NOT_READY: 'PAY_STRIPE_CHECKOUT_NOT_READY',
  /** 实时调用 Stripe 验证时 credential 无效 / 缺 Checkout Sessions 权限；HTTP 422。
   *  **不校验 Account 读取权限**——Account 归属一致性归 serving 侧按 operation 固化的 stripe_account_id 校验；
   *  details: { app_id: string; reason: 'CREDENTIAL_INVALID' | 'PERMISSION_DENIED';
   *  connection_status: 'invalid'; ready_for_activation: false; issues: { code: 'CONNECTION_INVALID' }[]; checked_at: string }。
   *  **不得**包含 restricted key、tail、hash、Account 响应或 Stripe 原始错误体 */
  PAY_STRIPE_CHECKOUT_CONNECTION_FAILED: 'PAY_STRIPE_CHECKOUT_CONNECTION_FAILED',
  /** Stripe 支付方式同步（ADM-PAY-S013，A8 §7.8.9）读取 Payment Method Configurations 失败；HTTP 422；
   *  details: { app_id: string; reason: 'CREDENTIAL_INVALID' | 'PERMISSION_DENIED' | 'DEFAULT_CONFIGURATION_MISSING' }。
   *  判定顺序沿用上面 ①②，③ 改为本码（上游不可达同为 SERVICE_UNAVAILABLE）。同步表保持上次结果，
   *  Checkout 配置的 connection_status / is_active 不变。**不得**包含 restricted key、Account 响应或 Stripe 错误体 */
  PAY_STRIPE_PAYMENT_METHOD_SYNC_FAILED: 'PAY_STRIPE_PAYMENT_METHOD_SYNC_FAILED',

  // ─────── 三方支付供应商（框架通道）配置（ADM-PAY-S012 / SRV-PAY-038，A8 §10）───────
  // details 分两种场景（dev-docs#1518）：配置场景（test / activate）带 app_id；回调 endpoint 登记场景
  // 不含 App，details 为 { channel, merchant_account_id, mode, … }、不带 app_id。
  /** 配置场景：该 App 在该通道无未软删配置（CONFIG_MISSING）或配置引用的 credential route 不可用
   *  （CREDENTIAL_ROUTE_MISSING），details: { app_id; channel; reason }；
   *  endpoint 场景：同一 (channel, merchant_account_id, mode) 下无未软删配置，
   *  details: { channel; merchant_account_id; mode; reason: 'CONFIG_MISSING' }。HTTP 422 */
  PAY_PROVIDER_NOT_CONFIGURED: 'PAY_PROVIDER_NOT_CONFIGURED',
  /** 配置场景：字段不完整（CONFIG_INCOMPLETE）/ mode 与环境不一致（MODE_MISMATCH）/ 环境未注入凭据加密
   *  master key（CREDENTIAL_STORE_UNAVAILABLE）/ activate 时配置不是 connected（CONNECTION_UNVERIFIED，
   *  activate 不跑探针）/ test 与 activate 时商户账户组没有当前有效验签材料（VERIFICATION_MATERIAL_MISSING），
   *  details: { app_id; channel; issues: { code }[] }；
   *  endpoint 场景（回调登记、验签材料写入）：登记的 mode 与环境不一致（MODE_MISMATCH）/ 材料写入时环境
   *  未注入 master key（CREDENTIAL_STORE_UNAVAILABLE），details: { channel; merchant_account_id; mode;
   *  issues: [{ code }] }。HTTP 422 */
  PAY_PROVIDER_NOT_READY: 'PAY_PROVIDER_NOT_READY',
  /** 连接测试中供应商明确拒绝凭据或权限；HTTP 422；details: { app_id; channel;
   *  reason: 'CREDENTIAL_INVALID' | 'PERMISSION_DENIED'; connection_status: 'invalid'; checked_at }。
   *  网络失败 / 超时 / 限流 / 5xx / 探针未登记的结果走 SERVICE_UNAVAILABLE。**不得**含凭据或供应商原始报文 */
  PAY_PROVIDER_CONNECTION_FAILED: 'PAY_PROVIDER_CONNECTION_FAILED',

  // ─────── BOOK 域（ADM-BOK-*） ───────
  /** 章节发布失败（前置章节未发布）；HTTP 422；details: { pending_chapter_ids } */
  BOOK_CHAPTER_PUBLISH_DEPENDENCY: 'BOOK_CHAPTER_PUBLISH_DEPENDENCY',
  /** 书籍下架但缺少英文下架原因；HTTP 422；details: 无 */
  BOOK_DELIST_REASON_REQUIRED: 'BOOK_DELIST_REASON_REQUIRED',
  /** active root 已有章节或译版后禁止改来源 identity；HTTP 422；
   * details: { reason: 'HAS_ACTIVE_CHAPTERS' | 'HAS_ACTIVE_TRANSLATION_CHILDREN' } */
  BOOK_SOURCE_IDENTITY_LOCKED: 'BOOK_SOURCE_IDENTITY_LOCKED',
  /** 免费章节数创建后不允许修改；HTTP 422；details: 无 */
  BOOK_FREE_CHAPTER_LOCKED: 'BOOK_FREE_CHAPTER_LOCKED',
  // ─────── USER 域（ADM-USR-*） ───────
  /** 用户已封禁（重复封禁请求）；HTTP 422；details: { banned_at, reason } */
  USER_ALREADY_BANNED: 'USER_ALREADY_BANNED',
  /** 用户未封禁（解禁请求未命中）；HTTP 422；details: 无 */
  USER_NOT_BANNED: 'USER_NOT_BANNED',
  /** 设备 ID 已在白名单；HTTP 422；details: { device_id } */
  USER_DEVICE_ALREADY_WHITELISTED: 'USER_DEVICE_ALREADY_WHITELISTED',
  /** PATCH /user-tags/:id/active 置 is_active=true 时 tag_key 未命中分群规则注册表；HTTP 422；details: { tag_key } */
  USER_TAG_RULE_UNBOUND: 'USER_TAG_RULE_UNBOUND',

  // ─────── FEEDBACK 域（ADM-FBK-*） ───────
  /** 反馈已关闭，不允许继续回复；HTTP 422；details: { closed_at } */
  FEEDBACK_CLOSED: 'FEEDBACK_CLOSED',
  /** 反馈处于不可回复终态（如 RESOLVED 已解决）；HTTP 422；details: { status } */
  FEEDBACK_NOT_REPLYABLE: 'FEEDBACK_NOT_REPLYABLE',
  /** 翻译服务调用失败；HTTP 422；details: { provider_error_code? } */
  FEEDBACK_TRANSLATION_FAILED: 'FEEDBACK_TRANSLATION_FAILED',

  // ─────── PUSH 域（ADM-PUSH-*） ───────
  /** 推送任务类型不允许运营主动创建；HTTP 422；details: { task_type } */
  PUSH_TASK_TYPE_NOT_MANUAL: 'PUSH_TASK_TYPE_NOT_MANUAL',
  /** 推送任务 task_type 经 TEMPLATE_SLUG_BY_TASK_TYPE 映射的模板未配置（push_templates 无 server-seeded 行；码名含 LOCALE 系历史命名、实际非缺语言）；HTTP 422；details: { template_slug }（对齐 dev-docs 21-Admin错误码段 §2.1） */
  PUSH_TEMPLATE_LOCALE_REQUIRED: 'PUSH_TEMPLATE_LOCALE_REQUIRED',
  /** 创建 topup_promo 推送任务携带的 offer_id 非该 App 下**投递时刻可服务** app_payment_offers
   *  （不存在 / 不属该 App / is_active=false / 已软删 / 投递时刻 `t=scheduled_at ?? now` ∉ `[starts_at, ends_at)`）；
   *  #228 决议 3 push offer 化写侧校验 + 张培 #244 决议——杜绝把推送挂到失效活动 / 定时投递到 offer 失效后 = 死支付链
   *  （对齐 ADM-PUSH-S003 @ dev-docs 660d6ac + banner OPS_BANNER_OFFER_NOT_FOUND 体例 + serving GET /payment/offers/:offer_id/methods 口径）。
   *  HTTP 422；details: { app_id: string | null; offer_id: string }。admin 自管业务码、需求级口径已在 660d6ac，
   *  正式登记进 admin 专属错误码段 + banner 码收口留后续 admin push endpoint 契约 PR（#170 / 张培 #244 决议 2）*/
  PUSH_OFFER_NOT_FOUND: 'PUSH_OFFER_NOT_FOUND',
  /** 推送任务非可取消态（仅 PENDING 待发布可取消；SENDING/COMPLETED/FAILED/CANCELLED 终态拒）；HTTP 422；details: { status } */
  PUSH_TASK_NOT_CANCELLABLE: 'PUSH_TASK_NOT_CANCELLABLE',
  /** 模板当前不可测试发送（A4 §7.4）；HTTP 422；details: { template_slug; reason: 'REQUIRES_REAL_EVENT' | 'TEMPLATE_INACTIVE' } */
  PUSH_TEST_SEND_NOT_SUPPORTED: 'PUSH_TEST_SEND_NOT_SUPPORTED',
  /** 测试发送收件人未持 internal_test_account 关联行（A4 §7.5，用户不存在同码）；HTTP 422；details: { app_id; user_id } */
  PUSH_TEST_RECIPIENT_NOT_ELIGIBLE: 'PUSH_TEST_RECIPIENT_NOT_ELIGIBLE',

  // ─────── FINANCE 域（ADM-FIN-*） ───────
  /** BONUS 发放金额非法（≤ 0 或超上限）；HTTP 422；details: { max_amount } */
  FINANCE_BONUS_AMOUNT_INVALID: 'FINANCE_BONUS_AMOUNT_INVALID',
  /** BONUS 发放缺用户所属 App 对应语言的原因文案；HTTP 422；details: { required_locale } */
  FINANCE_BONUS_REASON_LOCALE_REQUIRED: 'FINANCE_BONUS_REASON_LOCALE_REQUIRED',
  /** 退款告警事件已确认（重复 ack 请求）；HTTP 422；details: { acknowledged_at } */
  FINANCE_REFUND_ALERT_ALREADY_ACKED: 'FINANCE_REFUND_ALERT_ALREADY_ACKED',
  /** 交易人工归属冲突（POST /finance/transaction-imports/attribution，A1 §10）：目标行已自动归属
   *  （attribution_status=ATTRIBUTED），或 MANUALLY_ATTRIBUTED 后改绑不同 user 时任一投影已执行
   *  （entitlement=PROJECTED / credit·refund·finance_purchase·finance_refund effect=EXECUTED），
   *  或 CAS 与 reconcile 重放并发命中 0 行、重读判定投影已发生——财务效果已落地，纠错走退款/客服流程。
   *  HTTP 422；details: { attribution_status: string; projected: boolean }（回显当前归属状态 + 投影是否已发生）。
   *  客户端：展示当前归属与投影状态、禁用改绑表单。契约登记 dev-docs 21-Admin错误码段 §2.7 / A1 §10.4 */
  FINANCE_IMPORT_ATTRIBUTION_CONFLICT: 'FINANCE_IMPORT_ATTRIBUTION_CONFLICT',

  // ─────── OPS 域（ADM-OPS-*） ───────
  /** Banner 轮播图缺中英文必填标题；HTTP 422；details: { missing_locales } */
  OPS_BANNER_LOCALE_REQUIRED: 'OPS_BANNER_LOCALE_REQUIRED',
  /** Banner 位为系统预置位（is_seed_baseline），禁止删除（仅可上下架 + 改名/排序 + 配轮播图）；HTTP 422；details: { slug } */
  OPS_BANNER_BASELINE_LOCKED: 'OPS_BANNER_BASELINE_LOCKED',
  /** Banner 轮播图 target_type=offer 时 target_value=offer_id 非该 App 下 active app_payment_offers 行
   *  （不存在 / 非 active / 已软删 / 跨 App / 全局 banner 指 app-scoped offer）；#228 决议 3 banner offer 化写侧校验，
   *  避免挂失效活动 = 死链（dev-docs §3.3.2 校验口径）。banner→offer 删侧引用护栏由 ADM-PAY-S005 承接（DEFER:#166）。
   *  HTTP 422；details: { app_id: string | null; offer_id: string }。admin 自管业务码、不进 C 端字典 */
  OPS_BANNER_OFFER_NOT_FOUND: 'OPS_BANNER_OFFER_NOT_FOUND',
  /** 首页区块为系统默认，禁止删除（仅可编辑标题）；HTTP 422；details: 无 */
  OPS_HOME_SECTION_DEFAULT_LOCKED: 'OPS_HOME_SECTION_DEFAULT_LOCKED',
  /** App 层运营内容写入时，对应 `(module, app_id, language)` 尚无未软删的 app_dedicated_configs 身份行；
   * HTTP 422；details: { module: string; app_id: string; language: string }。
   * 客户端：先复制出该 App / 语言的独立配置，再编辑其中内容 */
  OPS_APP_DEDICATED_CONFIG_NOT_FOUND: 'OPS_APP_DEDICATED_CONFIG_NOT_FOUND',
  /** 复制时该三元身份已存在；HTTP 422；details: { module, app_id, language }. */
  OPS_APP_DEDICATED_CONFIG_EXISTS: 'OPS_APP_DEDICATED_CONFIG_EXISTS',
  /** 复制源为空或自动补齐任务未 success；HTTP 422；details: { module, language }. */
  OPS_GLOBAL_CONFIG_NOT_READY: 'OPS_GLOBAL_CONFIG_NOT_READY',
  /** 向不接受手选绑定的栏目写绑定（POST /ops/home-sections/:id/novels）。
   *  放行 = weekly_featured 且未软删，且属两类之一：全局默认栏（app_id IS NULL AND is_seed_baseline）
   *  或 App 层栏（app_id 非空）。HTTP 422；
   *  details: { home_section_id, section_id, strategy_key, app_id } */
  OPS_HOME_SECTION_NOT_MANUAL_BINDING: 'OPS_HOME_SECTION_NOT_MANUAL_BINDING',
  /** 同栏目同 novels.locale 未软删绑定已达上限 10。HTTP 422；
   *  details: { home_section_id, locale, limit, current } */
  OPS_HOME_SECTION_NOVEL_LIMIT_EXCEEDED: 'OPS_HOME_SECTION_NOVEL_LIMIT_EXCEEDED',
  /** FAQ / FAQ 分类为系统 baseline（is_seed_baseline=true，C 端 0016 seed），禁删（软删亦拒，仅可 is_active=false 下架）——
   *  服务侧先拦返干净业务码（对齐 C 端 0016 baseline-lock trigger）；slug（faq 深链）/ key（分类 FK 锚点）为创建后
   *  不可改，编辑省略、service pin（非 baseline 专属）。HTTP 422；
   *  details: { resource_type: 'faq' | 'faq_category'; id: string; slug?: string; key?: string }
   *  （resource_type + id 对齐 dev-docs 01-通用约定/21-Admin错误码段 §2.2；slug/key 为 additive）。已收录 dev-docs 21-Admin错误码段 §2.2 */
  OPS_FAQ_BASELINE_LOCKED: 'OPS_FAQ_BASELINE_LOCKED',
  /** 写 FAQ（POST/PATCH /ops/faqs）时 category_key 非该库 active faq_categories 行（不存在 / is_active=false / 已软删）→
   *  防配出 C 端渲染不认的死分类（category_title 从 faq_categories.title_i18n 渲染）。HTTP 422；
   *  details: { category_key: string }。admin 自管业务码、不进 C 端字典（dev-docs 待收录 21-Admin错误码段）*/
  OPS_FAQ_CATEGORY_NOT_FOUND: 'OPS_FAQ_CATEGORY_NOT_FOUND',
  /** App 独立 FAQ 所用分类缺少本行 language 的非空 title_i18n；HTTP 422。 */
  OPS_FAQ_CATEGORY_TITLE_MISSING: 'OPS_FAQ_CATEGORY_TITLE_MISSING',
  /** 清理分类某语言标题时仍有该语言 App 独立 FAQ 引用；HTTP 422。 */
  OPS_FAQ_CATEGORY_TITLE_IN_USE: 'OPS_FAQ_CATEGORY_TITLE_IN_USE',
  /** 删除 FAQ 分类（DELETE /ops/faq-categories/:id）时仍被**任一未软删 faq**（deleted_at IS NULL，含下架 is_active=false，
   *  不只 active——下架不解除引用，故补救不含「下架」）按 category_key 引用 → 拒删，先**迁移（改 category_key）/ 删除（软删）**
   *  引用的 FAQ（防孤儿 category_title）。HTTP 422；
   *  details: { category_id: string; key: string; references: { faq_id: string }[] }（对齐 dev-docs A2 §10.4）。
   *  admin 自管业务码、不进 C 端字典（dev-docs 待收录 21-Admin错误码段）*/
  OPS_FAQ_CATEGORY_IN_USE: 'OPS_FAQ_CATEGORY_IN_USE',
  /** FAQ slug 在同 scope 冲突（per-scope partial unique：COALESCE(app_id, zero_uuid) + slug WHERE deleted_at IS NULL）——
   *  全局层与 App 层可同 slug、软删后同 scope 同 slug 可再建，但同一 scope 内 active slug 唯一。HTTP 422；
   *  details: { app_id: string | null; slug: string }。admin 自管业务码、不进 C 端字典（dev-docs 待收录 21-Admin错误码段）*/
  OPS_FAQ_SLUG_CONFLICT: 'OPS_FAQ_SLUG_CONFLICT',
  /** upsert active 运营弹窗场景（is_active=true）前，该 App 按通道选择规则选中的通道无 active operational_popup
   *  paywall placement（ADM-OPS-S007，dev-docs #127 已确认 admin 自管码、不进 C 端字典）。
   *  选中通道 = apps.force_third_party_payment ? STRIPE : IAP（固定优先 IAP、不回退，对齐 GET /payment/popups
   *  serving 单通道规则，dev-docs 02-API契约/60 §2.1 + #93）；谓词 = app_payment_methods WHERE app_id AND
   *  scenario='operational_popup' AND payment_method=选中通道 AND is_active=true AND deleted_at IS NULL 不存在。
   *  避免建出无 paywall 落点的 active scene（C 端选不到 placement → 弹窗 data:null 失效）。
   *  HTTP 422；details: { app_id: string; payment_method: SettlementChannel（结算通道层，70 §3 两层闭集） }。
   *  客户端建议：先在该 App 为选中通道配置 / 启用 / 恢复 operational_popup 页的 Paywall placement（ADM-OPS-008）。
   *  与 ADM-PAY-S004 ③（删落点侧 PAY_PAGE_SCENARIO_REFERENCED）互为对偶 */
  POPUP_SCENE_PLACEMENT_MISSING: 'POPUP_SCENE_PLACEMENT_MISSING',

  // ─────── SYSTEM 域（ADM-SYS-*） ───────
  // ⚠️ 本 SYSTEM 是 **admin 自管命名空间**（ADM-SYS 系统配置域），与 C 端错误码字典保留的 SYSTEM 域
  //    （限流/降级/通用，如 SYSTEM_MAINTENANCE）**互不相干**：admin / C 端是独立 API，运行时不碰面，前缀同名仅巧合。
  /** 英文为 fallback 语言，不可停用（DB CHECK 兜底）；HTTP 422；details: { language_code } */
  SYSTEM_LANGUAGE_EN_LOCKED: 'SYSTEM_LANGUAGE_EN_LOCKED',
} as const;

export type AdminErrorCode = (typeof ADMIN_ERROR_CODES)[keyof typeof ADMIN_ERROR_CODES];
